import { logger } from '@user-portal/logger';
import { trackWorkoutCreateSuccess } from 'analytics/trackers/workout';
import { all, call, put, takeLatest } from 'redux-saga/effects';

import api from 'api';

import { handleErrorAction } from 'store/errors/actions';
import { fetchExercises } from 'store/exercises/actions';
import { navigateTo } from 'store/navigation/actions';
import { notifyError } from 'store/notifications/actions';

import { EXTENDED_DATE_FORMAT } from 'constants/date';
import { PATHS } from 'constants/routerPaths';

import { getToday } from 'helpers/date';
import { getBlocksMap, getOnlyExercises, getPlaylist } from 'helpers/workouts';

import { WorkoutAudioMapType, WorkoutAudioType, WorkoutPlanResponse, WorkoutResponse } from 'types/workouts';

import {
    createWorkout,
    getWorkoutById,
    getWorkoutPlan,
    setLoadingState,
    setWorkout,
    setWorkoutAudios,
    setWorkoutPlan,
    workoutComplete,
} from './actions';
import {
    COMPLETE_WORKOUT,
    CREATE_WORKOUT,
    GET_WORKOUT_AUDIOS,
    GET_WORKOUT_BY_ID,
    GET_WORKOUT_PLAN,
} from './actionTypes';

function* getWorkout({ payload }: ReturnType<typeof createWorkout>) {
    const { params, onError, returnTo, planHash } = payload;

    const { name, ...parameters } = params;

    try {
        const response: WorkoutResponse = yield call(api.workouts.buildWorkout, parameters);

        const workout = {
            ...response,
            planHash,
            created_at: getToday(EXTENDED_DATE_FORMAT),
        };

        const blocks = getBlocksMap(workout);
        const playlist = getPlaylist(workout);
        const exercises = getOnlyExercises(playlist);

        yield put(
            setWorkout({
                workout,
                blocks,
                playlist,
                exercises,
                name,
            })
        );

        const workoutConfiguration = {
            equipment: params.equipments,
            duration: params.workout_time,
            area: params.target_areas,
            difficulty: params.workout_fitness_level,
            is_warmup: params.warm_up,
            is_cooldown: params.cool_down,
        };

        trackWorkoutCreateSuccess(workoutConfiguration, exercises.length.toString());

        yield put(fetchExercises(workout.id));
        yield put(setLoadingState(false));

        yield put(navigateTo({ url: PATHS.WORKOUT_PREVIEW, options: { state: { returnTo } } }));
    } catch (error) {
        yield put(notifyError('basics.appError'));
        yield put(setLoadingState(false));
        yield put(handleErrorAction(error, payload));
        onError && onError();
    }
}

function* fetchWorkoutById({ payload }: ReturnType<typeof getWorkoutById>) {
    const { id, returnTo, planHash } = payload;

    try {
        const response: WorkoutResponse = yield call(api.workouts.getWorkoutById, id);

        const workout = {
            ...response,
            planHash,
            created_at: getToday(EXTENDED_DATE_FORMAT),
        };

        const blocks = getBlocksMap(workout);
        const playlist = getPlaylist(workout);
        const exercises = getOnlyExercises(playlist);

        yield put(setWorkout({ workout, blocks, playlist, exercises, name: payload.name }));
        yield put(fetchExercises(id));
        yield put(setLoadingState(false));
        yield put(navigateTo({ url: PATHS.WORKOUT_PREVIEW, options: { state: { returnTo } } }));
    } catch (error) {
        yield put(notifyError('basics.appError'));
        yield put(setLoadingState(false));
        yield put(handleErrorAction(error));
    }
}

function* completeWorkout({ payload }: ReturnType<typeof workoutComplete>) {
    const { params, onSuccess, onError } = payload;

    try {
        yield call(api.workouts.complete, params);

        onSuccess && onSuccess();
    } catch (error) {
        yield put(notifyError('basics.appError'));
        yield put(setLoadingState(false));
        yield put(handleErrorAction(error, payload));

        onError && onError();
    }
}

function* getWorkoutAudios() {
    try {
        const audiosResponse: WorkoutAudioType[] = yield call(api.workouts.getWorkoutAudios);

        const map: WorkoutAudioMapType = { unknown: [] };

        audiosResponse.forEach((audio) => {
            if (!audio) return;

            if (!audio.category_name) {
                map.unknown.push(audio);

                return;
            }

            if (!map[audio.category_name]) {
                map[audio.category_name] = [audio];
            } else {
                map[audio.category_name].push(audio);
            }
        });

        yield put(setWorkoutAudios(map));
    } catch (error) {
        yield put(handleErrorAction(error));
    }
}

function* getPlan({ payload }: ReturnType<typeof getWorkoutPlan>) {
    const { date } = payload;

    try {
        yield put(setLoadingState(true));
        const response: WorkoutPlanResponse = yield call(api.workouts.getPlan, date);

        yield put(setWorkoutPlan(response.workouts));
        yield put(setLoadingState(false));
    } catch (error) {
        logger.error('MB Portal:Get workout plan error', { error });
        yield put(setLoadingState(false));
        yield put(handleErrorAction(error));
    }
}

export default function* watchWorkouts() {
    yield all([
        takeLatest(CREATE_WORKOUT, getWorkout),
        takeLatest(GET_WORKOUT_BY_ID, fetchWorkoutById),
        takeLatest(COMPLETE_WORKOUT, completeWorkout),
        takeLatest(GET_WORKOUT_AUDIOS, getWorkoutAudios),
        takeLatest(GET_WORKOUT_PLAN, getPlan),
    ]);
}
